import React, { useEffect, useState } from 'react';

export type PropertyAssignmentValue = { property_ids: string[]; primary_property_id: string | null };

export function buildAssignmentPayload(selectedIds: string[], primaryId: string | null): PropertyAssignmentValue {
  const property_ids = Array.from(new Set(selectedIds.filter(Boolean)));
  return {
    property_ids,
    primary_property_id: primaryId && property_ids.includes(primaryId) ? primaryId : property_ids[0] ?? null,
  };
}

type Props = {
  properties: { id: string; name: string }[];
  selectedIds: string[];
  primaryId: string | null;
  onSave(value: PropertyAssignmentValue): Promise<void>;
  compact?: boolean;
  mode?: 'save' | 'draft' | 'autosave';
};

export const PropertyAssignmentPicker: React.FC<Props> = ({ properties, selectedIds, primaryId, onSave, compact, mode = 'save' }) => {
  const [selected, setSelected] = useState<string[]>(selectedIds);
  const [primary, setPrimary] = useState<string | null>(primaryId);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSelected(selectedIds); setPrimary(primaryId); }, [selectedIds, primaryId]);

  const persist = async (next: string[], nextPrimary: string | null) => {
    setSaving(true);
    try { await onSave(buildAssignmentPayload(next, nextPrimary)); }
    finally { setSaving(false); }
  };
  const toggle = (id: string, checked: boolean) => {
    const next = checked ? Array.from(new Set([...selected, id])) : selected.filter((value) => value !== id);
    const nextPrimary = next.includes(String(primary)) ? primary : next[0] ?? null;
    setSelected(next);
    setPrimary(nextPrimary);
    if (mode === 'draft') void onSave(buildAssignmentPayload(next, nextPrimary));
    if (mode === 'autosave') void persist(next, nextPrimary);
  };
  const choosePrimary = (id: string) => {
    setPrimary(id);
    if (mode === 'draft') void onSave(buildAssignmentPayload(selected, id));
    if (mode === 'autosave') void persist(selected, id);
  };
  const save = async () => {
    setSaving(true);
    try { await onSave(buildAssignmentPayload(selected, primary)); }
    finally { setSaving(false); }
  };

  return <fieldset className={compact ? 'space-y-1' : 'space-y-2'}>
    <legend className="text-xs font-semibold text-gray-600">Assigned properties</legend>
    <div className="max-h-52 overflow-auto rounded border border-gray-200 bg-white p-2 space-y-1">
      {properties.map((property) => {
        const checked = selected.includes(String(property.id));
        return <div key={property.id} className="flex items-center justify-between gap-3 py-1">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={checked} onChange={(event) => toggle(String(property.id), event.target.checked)}
              aria-label={`Assign ${property.name}`} />
            <span>{property.name}</span>
          </label>
          {checked && <label className="flex items-center gap-1 text-xs text-gray-500">
            <input type="radio" name="primary-property" checked={primary === String(property.id)}
              onChange={() => choosePrimary(String(property.id))} aria-label={`Make ${property.name} primary`} /> Primary
          </label>}
        </div>;
      })}
      {!properties.length && <p className="text-xs text-gray-400">No properties are available in your scope.</p>}
    </div>
    {mode === 'draft' ? <p className="text-xs text-gray-500">Saved when you finish setup</p> : mode === 'autosave' ? <p className="text-xs text-gray-500" aria-live="polite">{saving ? 'Saving…' : 'Changes save automatically'}</p> : <button type="button" onClick={save} disabled={saving}
      className="rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
      aria-label="Save property assignments">{saving ? 'Saving…' : 'Save assignments'}</button>}
  </fieldset>;
};
